import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useRef, useState } from 'react';
import { ScanLine, Search } from '../../lib/icons';
import { useNavigate } from 'react-router-dom';

import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { findProductIdByEan, searchProductSuggestions } from '../products/api';
import { HighlightedProductName } from '../products/HighlightedProductName';
import { isFastHidEan } from './hidScanner';
import './scanner.css';

const BarcodeScannerDialog = lazy(() =>
  import('./BarcodeScannerDialog').then((module) => ({
    default: module.BarcodeScannerDialog,
  })),
);

export function GlobalProductSearch({ canCreate }: { canCreate: boolean }) {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [error, setError] = useState('');
  const [searching, setSearching] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const keyTimes = useRef<number[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const scanButtonRef = useRef<HTMLButtonElement>(null);
  const debouncedValue = useDebouncedValue(value.trim(), 250);
  const suggestions = useQuery({
    queryKey: ['global-product-suggestions', debouncedValue],
    queryFn: () => searchProductSuggestions(debouncedValue, 6),
    enabled: debouncedValue.length >= 2,
  });

  const openSuggestion = (productId: string) => {
    setSuggestionsOpen(false);
    setValue('');
    setActiveSuggestion(-1);
    void navigate(`/izdelki/${productId}`);
  };

  const closeScanner = () => {
    setScannerOpen(false);
    window.requestAnimationFrame(() => scanButtonRef.current?.focus());
  };

  const openManualSearch = () => {
    setScannerOpen(false);
    setValue('');
    setError('');
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const submit = async (isHid: boolean) => {
    const query = value.trim();
    if (!query) return;
    const selected = suggestions.data?.[activeSuggestion];
    if (!isHid && selected) {
      openSuggestion(selected.id);
      return;
    }
    setError('');
    if (!isHid && !/^\d{8,14}$/.test(query)) {
      setValue('');
      setSuggestionsOpen(false);
      keyTimes.current = [];
      void navigate(`/izdelki?iskanje=${encodeURIComponent(query)}`);
      return;
    }
    setSearching(true);
    try {
      const productId = await findProductIdByEan(query);
      if (productId) {
        setValue('');
        setSuggestionsOpen(false);
        void navigate(`/izdelki/${productId}`);
      } else setError('Izdelek s to kodo ne obstaja.');
    } catch {
      setError('Izdelka ni bilo mogoče poiskati.');
    } finally {
      setSearching(false);
      keyTimes.current = [];
    }
  };

  return (
    <div className="global-search-wrap">
      <form
        className="global-search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          void submit(isFastHidEan(value, keyTimes.current, performance.now()));
        }}
        onBlur={(event) => {
          if (!event.currentTarget.parentElement?.contains(event.relatedTarget))
            setSuggestionsOpen(false);
        }}
      >
        <Search size={18} aria-hidden="true" />
        <input
          id="global-product-search"
          ref={inputRef}
          autoFocus
          autoComplete="off"
          inputMode="search"
          enterKeyHint="search"
          role="combobox"
          aria-label="Išči izdelek po SKU, imenu ali EAN"
          aria-autocomplete="list"
          aria-expanded={suggestionsOpen && Boolean(suggestions.data?.length)}
          aria-controls="global-product-suggestions"
          aria-activedescendant={
            activeSuggestion >= 0
              ? `global-product-suggestion-${activeSuggestion}`
              : undefined
          }
          value={value}
          onFocus={(event) => {
            event.currentTarget.select();
            keyTimes.current = [];
            setSuggestionsOpen(true);
          }}
          onChange={(event) => {
            setValue(event.target.value);
            setError('');
            setSuggestionsOpen(true);
            setActiveSuggestion(-1);
          }}
          onKeyDown={(event) => {
            if (event.key.length === 1 && !event.ctrlKey && !event.metaKey)
              keyTimes.current.push(performance.now());
            if (event.key === 'Backspace' || event.key === 'Delete')
              keyTimes.current = [];
            if (event.key === 'ArrowDown' && suggestions.data?.length) {
              event.preventDefault();
              setSuggestionsOpen(true);
              setActiveSuggestion((current) =>
                Math.min(current + 1, suggestions.data.length - 1),
              );
            }
            if (event.key === 'ArrowUp' && suggestions.data?.length) {
              event.preventDefault();
              setActiveSuggestion((current) => Math.max(current - 1, 0));
            }
            if (event.key === 'Escape') {
              setSuggestionsOpen(false);
              setActiveSuggestion(-1);
            }
          }}
        />
        {searching && <span className="global-search__busy">Iskanje …</span>}
        <button
          className="global-search__scan"
          type="button"
          aria-label="Odpri skener črtnih kod"
          ref={scanButtonRef}
          onClick={() => setScannerOpen(true)}
        >
          <ScanLine size={21} />
        </button>
      </form>
      {suggestionsOpen &&
        debouncedValue.length >= 2 &&
        debouncedValue === value.trim() && (
          <div
            className="global-search__suggestions"
            id="global-product-suggestions"
            role="listbox"
            aria-label="Predlogi izdelkov"
          >
            {suggestions.isPending && <p>Iskanje predlogov …</p>}
            {suggestions.isError && (
              <p role="alert">Predlogov ni bilo mogoče naložiti.</p>
            )}
            {suggestions.data?.map((product, index) => (
              <button
                id={`global-product-suggestion-${index}`}
                role="option"
                aria-selected={activeSuggestion === index}
                className={activeSuggestion === index ? 'is-active' : ''}
                type="button"
                key={product.id}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => openSuggestion(product.id)}
              >
                <span>
                  <strong>{product.sku}</strong>
                  <small>
                    <HighlightedProductName
                      name={product.name}
                      query={debouncedValue}
                    />
                  </small>
                </span>
              </button>
            ))}
            {suggestions.data?.length === 0 && <p>Ni podobnih izdelkov.</p>}
          </div>
        )}
      {error && (
        <div className="global-search__error" role="alert">
          <span>{error}</span>
          {canCreate && /^\d+$/.test(value.trim()) && (
            <button
              type="button"
              onClick={() => {
                void navigate(
                  `/izdelki/nov?ean=${encodeURIComponent(value.trim())}`,
                );
              }}
            >
              Ustvari nov izdelek
            </button>
          )}
        </div>
      )}
      {scannerOpen && (
        <Suspense
          fallback={<div className="scanner-loading">Nalaganje skenerja …</div>}
        >
          <BarcodeScannerDialog
            canCreate={canCreate}
            onClose={closeScanner}
            onManualSearch={openManualSearch}
          />
        </Suspense>
      )}
    </div>
  );
}
